<template>
  <v-row
    v-show="isDisplayed"
    class="SigningChecklistTableItem my-2 position-relative"
    data-locator="signing-checklist-file-row"
    :data-name="item.title"
  >
    <template v-if="isRefreshing">
        <v-col cols="5">
          <div>
            <v-skeleton-loader type="text"/>
          </div>
          <div>
            <v-skeleton-loader type="actions"/>
          </div>
        </v-col>
        <v-col cols="2">
          <v-skeleton-loader type="text@5"/>
        </v-col>
        <v-col cols="2">
          <v-skeleton-loader type="text@5"/>
        </v-col>
        <v-col cols="2">
          <v-skeleton-loader type="button"/>
        </v-col>
        <v-col class="text-right" cols="1">
          <v-skeleton-loader type="avatar"/>
        </v-col>
    </template>
    <template v-else>
      <v-col :cols="hasSigningOrder ? 4 : 5">
        <div class="d-flex align-center column-gap-4">
          <template v-if="isPm && $vuetify.display.mdAndUp">
            <div class="d-flex align-center column-gap-1 checkbox-drag-icon-wrapper">
              <v-checkbox :model-value="isStepSelected"
                        hide-details
                        readonly
                        data-locator="select-checkbox"
                        :ripple="false"
                        @click.stop="onSelectedStepClick"
              />
              <app-icon-ds
                data-locator="drag-icon"
                icon="grip-vertical"
                weight="fas"
                class="SigningChecklistTable-dragIcon dragIcon text-grey-400"
                size="xs"
                style="cursor: grab"
              />
            </div>
          </template>
          <div class="item-name">
            <div class="d-flex align-center column-gap-2 mb-3" data-locator="file-name">
              <img
                class="ClosdFildersIcon"
                style="width: 20px"
                src="@/assets/img/icons/pdf.svg"
                alt="File icon"
              />
              <span>
                {{ computedTitle }}
              </span>
              <template v-if="item.canWrite && (item.envelope.status === 'draft' || item.envelope.status === 'temp')">
                <SigningChecklistTableItemStatusTooltip
                  :reasons-for-blocking-sending="item.envelope.reasonsForBlockingSending"
                  :reasons-for-blocking-signing="item.envelope.reasonsForBlockingSigning"
                  :reason-for-warning-sending="item.envelope.reasonForWarningSending"
                  data-locator="alert-tooltip"
                />
              </template>
            </div>
            <div class="d-flex align-center justify-space-between">
              <SigningChecklistTableItemSignatureChips :item="item" />
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="2" :data-locator="`${headerSelectedColumn.value}-list`">
        <div class="d-flex d-sm-none row-gap-2 mb-2">
          <template v-if="isPm">
            <AppSelect v-model="headerSelectedColumnModel"
                        class="SigningChecklistTableItem-select"
                        no-label
                        return-object
                        style="width: 100px;"
                        :items="headerSelectItems"
                        item-title="text"
                        item-value="value"
            />
          </template>
          <template v-else>
            <div class="text--bold">{{ headerSelectedColumn.text }}</div>
          </template>
        </div>
        <template v-if="headerSelectedColumn.value === 'signatories'">
          <SigningChecklistMultiEnvelopeSignatoriesList v-if="isMultiEnvelope" :signatories="item.envelope.signatories" />
          <div v-else>
            <div v-for="(signersGroup, groupIndex) in item.envelope.signatories"
                :key="`envelope-${item.envelope.id}-signer-group-${groupIndex}`"
            >
              <div v-for="signer in signersGroup" :key="`envelope-${item.envelope.id}-signer-${signer.id}`">
                  <div class="d-flex align-center">
                  <template v-if="signer.status === 'sent'">
                    <app-icon icon-name="clock" icon-weight="far" size="small" class="d-inline-flex mr-1 text-tertiary"/>
                  </template>
                  <template v-else-if="signer.status === 'signed'">
                    <app-icon icon-name="circle-check" icon-weight="fas" size="small" class="d-inline-flex mr-1 text-success"/>
                  </template>
                  <template v-else>
                    <app-icon icon-name="clock" icon-weight="far" size="small" class="d-inline-flex mr-1 text-grey-400"/>
                  </template>
                  <span v-if="item.envelope.signatories.length > 1" class="mr-1">
                    {{ `${groupIndex + 1} -`  }}
                  </span>
                  {{ signer.fullName }}
                  <template v-if="signer.id === profile.id && item.envelope.status === 'sent' && !signer.signed">
                    <PdfSigningDialogButton v-if="item.envelope.provider === 'pdf'"
                                            :step="item"
                                            class="d-inline-block ml-1"
                                            :signer-id="signer.id"
                    />
                    <app-button
                      v-else-if="signer.status === 'sent'"
                      size="small"
                      type="tonal"
                      class="ml-1"
                      data-locator="sign-button"
                      @click="$router.push({
                        name: SIGNING_VIEW_ROUTE_NAME,
                        params: {
                          envelopeId: item.envelope.id,
                        },
                      })"
                    >
                      {{ $t('project.signing-checklist.list.SigningChecklistTableItem.signButtonLabel') }}
                    </app-button>
                  </template>
                </div>
                <template v-if="hasSignerWitness(signer.id)">
                  <div v-for="witness in getSignerWitness(signer.id)" :key="witness.id" class="ml-6 d-flex">
                    <AppTooltip>
                      <template #activator="{props}">
                        <div v-bind="props">
                            <app-icon v-if="witness.isSigned" icon-name="circle-check" icon-weight="fas" size="small" class="d-inline-flex mr-1 text-success"/>
                            <app-icon v-else-if="signer.status === 'sent' || signer.status === 'signed'" icon-name="clock" icon-weight="far" size="small" class="d-inline-flex mr-1 text-tertiary"/>
                            <app-icon v-else icon-name="clock" icon-weight="far" size="small" class="d-inline-flex mr-1 text-grey-400"/>
                            {{ witness.signer.status }}{{ `${witness.signer.fullName}` }}
                        </div>
                      </template>
                      <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.witnessOf', {name: signer.fullName}) }}</span>
                    </AppTooltip>
                    <AppTooltip v-if="signer.status === 'signed' && !witness.isSigned && witness.signer.id === profile.id"  top>
                      <template #activator="{props}">
                          <app-button
                            v-bind="props"
                            size="small"
                            type="tonal"
                            class="ml-1"
                            data-locator="sign-button"
                            @click="handleSignAsWitness(item.envelope.id)"
                          >
                            {{ $t('project.signing-checklist.list.SigningChecklistTableItem.signButtonLabel') }}
                      </app-button>
                      </template>
                      <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.signAsWitness')}}</span>
                    </AppTooltip>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
        <template v-if="headerSelectedColumn.value === 'recipients'">
          <div v-for="recipient in item.envelope.recipients"
                :key="`envelope-${item.envelope.id}-recipient-${recipient.id}`"
          >
            - {{ recipient.fullName }}
          </div>
        </template>
        <template v-if="headerSelectedColumn.value === 'observers'">
          <div v-for="observer in item.envelope.observers"
                :key="`envelope-${item.envelope.id}-observer-${observer.id}`"
          >
            - {{ observer.fullName }}
          </div>
        </template>
      </v-col>
      <v-col cols="2" data-locator="validators-list">
        <div class="d-block d-sm-none text--bold mb-2">{{ $t(`project.signing-checklist.list.SigningChecklistTableItem.validators`) }}</div>
        <div v-for="validation in item.envelope.validations"
              :key="`envelope-${item.envelope.id}-validator-${validation.id}`"
        >
          <template v-if="!validation.validated">
            <font-awesome-icon :icon="['far', 'clock']"
                                class="text-tertiary"
            ></font-awesome-icon>
            {{ validation.validator.fullName }}
          </template>
          <template v-if="validation.validated">
            <div class="d-flex align-center flex-wrap">
              <AppTooltip>
                <template #activator="{props}">
                  <div v-bind="props" class="mr-4">
                    <font-awesome-icon :icon="['fas', 'circle-check']"
                                        class="text-success"
                    ></font-awesome-icon>
                    {{ validation.validator.fullName }}
                  </div>
                </template>
                <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.validatedTooltip', { date: getValidationDate(validation.validatedAt) }) }}</span>
              </AppTooltip>

              <div v-if="validation.attachment" class="attachment-file-actions">
                <AppDownloadFile :link="validation.attachment.url"
                >
                  <template #default="{ submitForm }">
                    <AppTooltip>
                      <template #activator="{props}">
                        <div v-bind="props">
                          <app-button
                            size="small"
                            type="icon"
                            @click="submitForm"
                          >
                            <app-icon
                              icon-name="file-download"
                              icon-weight="fal"
                              size="small"
                            />
                          </app-button>
                        </div>
                      </template>
                      <span>{{ $t('common.download') }}</span>
                    </AppTooltip>
                  </template>
                </AppDownloadFile>

                <AppTooltip>
                  <template #activator="{props}">
                    <div v-bind="props">
                      <app-button
                        v-if="isPm"
                        size="small"
                        type="icon"
                        @click="() => openShareAttachmentFilePane(validation.attachment)"
                      >
                        <app-icon
                          icon-name="share"
                          icon-weight="fal"
                          size="small"
                        />
                      </app-button>
                    </div>
                  </template>
                  <span>{{ $t('common.share') }}</span>
                </AppTooltip>
              </div>
            </div>
          </template>
          <div v-if="validation.canValidate && !validation.validated && item.envelope.status !== 'voided'">
            <SigningChecklistTableItemValidateDialog :mnemo="mnemo"
                                                      :envelopeId="item.envelope.id"
                                                      :validationId="validation.id"
                                                      :stepId="item.id"
                                                      :parent="parent"
            />
          </div>
          <div
            v-if="validation.canValidate && validation.validated && item.envelope.status !== 'voided' && item.envelope.status !== 'signed'">
            <SigningChecklistTableItemInvalidateDialog :mnemo="mnemo"
                                                        :envelopeId="item.envelope.id"
                                                        :validationId="validation.id"
                                                        :stepId="item.id"
                                                        :parent="parent"
            />
          </div>
        </div>
      </v-col>
      <v-col class="SigningChecklistTableItem-status d-flex align-center"
              :class="`SigningChecklistTableItem-status--${item.envelope.status}`"
              cols="2"
      >
        <div>
          <div class="d-block d-sm-none text--bold mb-2">{{ $t(`common.status`) }}</div>
          <v-chip :color="envelopeStatusColor"
                  label
                  class="mb-2"
                  data-locator="file-status"
                  variant="flat"
          >
            {{ $t(`common.envelopeStatus.${item.envelope.status}`) }}
          </v-chip>
          <template v-if="item.envelope.status === 'error'">
            <div class="text--x-small text-error">
              <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.envelopeStatusErrorMessage') }}</span>
              <a :href="errorStatusFaqLink" target="_blank" class="text-error ml-2" v-if="isErrorStatusFaqLinkDisplayed"><app-icon-ds icon="external-link" size="xxs" /></a>
            </div>
          </template>
          <template v-else-if="envelopeStatusDate">
            <div data-locator="status-date">{{ envelopeStatusDate }}</div>
          </template>
        </div>
      </v-col>
      <v-col v-if="hasSigningOrder" cols="1">
        <b v-if="item.signingGroup">#{{ item.signingGroup }}</b>
      </v-col>
      <v-col class="text-right d-flex justify-end align-center" cols="1">
        <AppDownloadFile :link="envelopeResourceLink">
          <template #default="{submitForm}">
            <AppTooltip>
              <template #activator="{props}">
                <app-button
                  tyle="icon"
                  size="small"
                  class="ml-2"
                  data-locator="download-button"
                  variant="neutral"
                  v-bind="props"
                  @click="submitForm"
                >
                  <app-icon icon-name="download" icon-weight="fas" size="medium" class="text-accent-lighten-2"/>
                </app-button>
            </template>
            <span>{{$t('common.download')}}</span>
          </AppTooltip>
        </template>
        </AppDownloadFile>
        <AppDownloadFile :link="envelopeResourceLink" forceReader>
          <template #default="{submitForm}">
            <AppTooltip>
              <template #activator="{props}">
                <app-button type="icon"
                            size="small"
                            data-locator="preview-button"
                            v-bind="props"
                            @click="submitForm"
                >
                  <app-icon icon-name="eye" icon-weight="fas" class="text-accent-lighten-2"/>
                </app-button>
              </template>
              <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.previewTooltip') }}</span>
            </AppTooltip>
          </template>
        </AppDownloadFile>
        <div v-if="item.canWrite && envelopeMenu.length !== 0 && isPm">
            <AppTooltip>
            <template #activator="{props}">
              <div v-bind="props">
                <AppMenu :menu="envelopeMenu" v-bind="props">
                  <template #activator="{props}">
                    <app-button
                      type="icon"
                      size="small"
                      v-bind="props"
                      data-locator="other-actions-menu-button"
                    >
                      <app-icon icon-name="ellipsis-vertical" class="text-body-1"/>
                    </app-button>
                  </template>
                </AppMenu>
              </div>
            </template>
            <span>{{ $t('common.additionalActions') }}</span>
          </AppTooltip>
        </div>
      </v-col>
    </template>
    
    <div v-show="floatingAdd && isPm">
      <AppMenu
        v-model="floatingMenuIsOpened"
        :menu="floatingMenu"
        :offset="-20"
      >
        <template #activator="{ props }">
          <!-- We use a bare v-tooltip here because AppTooltip has trouble when combined with another activator -->
          <v-tooltip transition="fade-transition" :open-delay="100">
            <template #activator="{props: tooltipProps}">
              <font-awesome-icon
                v-bind="{...tooltipProps, ...props}"
                :icon="['fas', 'map-marker-plus']"
                class="fa-rotate-270 marker"
              ></font-awesome-icon>
            </template>
            <span>{{ $t('project.signing-checklist.list.SigningChecklistTableItem.floatingAddTooltip') }}</span>
          </v-tooltip>
        </template>
      </AppMenu>
    </div>

    <QualifiedDSSignatureWarningDialog v-if="qualifiedDSWarningDialog"
                                       @close="qualifiedDSWarningDialog = false"
                                       @confirm="confirmQualifiedSignatureWarning"
    />
  </v-row>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppMenu from '@/common/AppMenu.vue'
import AppSelect from '@/common/AppSelect.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { ISOToShortDate, ISOToShortenedDate } from '@/common/utils/dates'
import {
  canShowSendStep,
  canShowCancelStep,
  canShowDeleteStep,
  canShowResetToDraftStep,
  canShowValidateStep,
  canShowSignTagsStep,
  canShowReadOnlyStep,
  canSwapFile,
} from '@/common/utils/signingChecklist'
import { SIGNING_CHECKLIST_ADD_ROUTE_NAME, SIGNING_VIEW_ROUTE_NAME } from '@/router'
import {
  REFRESH_STEP,
  VALIDATE_THEN_SEND_NOW,
  CREATE_TITLE,
  REFRESH_SIGNING_CHECKLIST,
  OPEN_SHARE_ATTACHMENT_FILE_PANE,
} from '@/store/modules/signing-checklist/action_types'
import {
  SET_CURRENT_TITLE_EDIT,
  REMOVE_REFRESH_STEP_PENDING,
  SET_CANCEL_STEP_MODAL,
  SET_DELETE_STEP_MODAL,
  SET_SWAP_FILES,
  ADD_REFRESH_STEP_PENDING,
  TOGGLE_SELECTED_STEP,
  SET_SEND_SIGN_STEP_MODAL,
  SET_VOIDED_TO_DRAFT_STEP_MODAL,
} from '@/store/modules/signing-checklist/mutation_types'

import SigningChecklistMultiEnvelopeSignatoriesList from './SigningChecklistMultiEnvelopeSignatoriesList.vue'
import SigningChecklistTableItemInvalidateDialog from './SigningChecklistTableItemInvalidateDialog.vue'
import SigningChecklistTableItemSignatureChips from './SigningChecklistTableItemSignatureChips.vue'
import SigningChecklistTableItemStatusTooltip from './SigningChecklistTableItemStatusTooltip.vue'
import SigningChecklistTableItemValidateDialog from './SigningChecklistTableItemValidateDialog.vue'
import QualifiedDSSignatureWarningDialog from '../dialogs/QualifiedDSSignatureWarningDialog.vue'
import PdfSigningDialogButton from '../pdf-signing/PdfSigningDialogButton.vue'

const {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} = createNamespacedHelpers('signingChecklist')
const { mapState: mapUserState, mapGetters: mapUserGetters } = createNamespacedHelpers('user')
export default {
  name: 'SigningChecklistTableItem',
  components: {
    AppDownloadFile,
    AppMenu,
    AppSelect,
    AppTooltip,
    PdfSigningDialogButton,
    QualifiedDSSignatureWarningDialog,
    SigningChecklistMultiEnvelopeSignatoriesList,
    SigningChecklistTableItemInvalidateDialog,
    SigningChecklistTableItemSignatureChips,
    SigningChecklistTableItemStatusTooltip,
    SigningChecklistTableItemValidateDialog,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
    headerSelectedColumn: {
      type: Object,
      required: true,
    },
    headerSelectItems: {
      type: Array,
      required: true,
    },
    parent: {
      type: Object,
      required: true,
    },
    floatingAdd: {
      type: Boolean,
      required: true,
    },
    hasSigningOrder: {
      type: Boolean,
      default: false,
    },
    isMatchingFilters: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      SIGNING_VIEW_ROUTE_NAME,
      errorStatusFaqLink: 'https://help.closd.com/fr/knowledge-base/comment-vous-debloquer-si-votre-document-est-rejete/',
      floatingMenuIsOpened: false,
      pdfFile: null,
      qualifiedDSWarningDialog: false,
    }
  },
  watch: {
    floatingAdd (value) {
      if (!value) {
        this.floatingMenuIsOpened = false
      }
    },
  },
  computed: {
    ...mapUserState(['profile']),
    ...mapUserGetters(['isSubPayingUser']),
    ...mapState(['refreshStepPendingIds', 'selectedSteps', 'createTitlePending', 'qualifiedDSWarningIsMuted']),
    ...mapGetters(['isSigningChecklistFiltered']),
    isPm () {
      return this.$store.getters['room/isCurrentUserPm']
    },
    headerSelectedColumnModel: {
      get () {
        return this.headerSelectedColumn
      },
      set (value) {
        this.$emit('update:headerSelectedColumn', value)
      },
    },
    envelopeResourceLink () {
      return `${import.meta.env.VUE_APP_API_URL}/room/${this.mnemo}/downloads/envelope/${this.item.envelope.id}`
    },
    envelopeStatusColor () {
      switch (this.item.envelope.status) {
        case 'draft':
        case 'temp':
        case 'sending':
          return '#e0e0e0'
        case 'queued':
        case 'sent':
        case 'unvalidated':
          return 'warning'
        case 'signed':
          return 'success'
        case 'error':
        case 'voided':
          return 'error'
      }
      return null
    },
    envelopeStatusDate () {
      if (this.item.envelope.timestamp) {
        return `${this.$t('project.signing-checklist.list.SigningChecklistTableItem.envelopeStatusDate')} ${ISOToShortDate(this.item.envelope.timestamp)}`
      } else if (this.item.date) {
        return `${this.$t('project.signing-checklist.list.SigningChecklistTableItem.envelopeDueDate')} ${ISOToShortenedDate(this.item.date)}`
      }
      return null
    },
    envelopeMenu () {
      const menu = []
      if (canShowDeleteStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.delete'),
          locatorValue: 'delete-menu-item',
          action: () => this.SET_DELETE_STEP_MODAL([this.item.id]),
        })
      }

      if (this.isSubPayingUser) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.saveAsTemplate'),
          locatorValue: 'save-as-template-menu-item',
          action: () => { this.$emit('save-as-template', this.item.envelope) },
        })
      }

      if (canSwapFile(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.swapFile'),
          locatorValue: 'swap-file-menu-item',
          action: () => {
            this.SET_SWAP_FILES({
              ids: [this.item.id],
              steps: [this.item],
            })
          },
        })
      }

      if (this.item.status !== 'sending') {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.settings'),
          locatorValue: 'settings-menu-item',
          action: () => {
            this.$router.push({
              name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
              query: {
                step: 2,
                stepIds: this.item.id,
              },
            })
          },
        })
      }
      if (canShowSignTagsStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.signatureTags'),
          locatorValue: 'signature-tags-menu-item',
          action: () => {
            this.$router.push({
              name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
              query: {
                step: 3,
                stepIds: this.item.id,
              },
            })
          },
        })
      }
      if (canShowReadOnlyStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.readOnly'),
          locatorValue: 'read-only-signature-tags-menu-item',
          action: () => {
            this.$router.push({
              name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
              query: {
                step: 3,
                stepIds: this.item.id,
              },
            })
          },
        })
      }
      if (canShowValidateStep(this.item) && !this.item.envelope.missingToSend) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.validateThenSend'),
          locatorValue: 'validate-then-send-menu-item',
          action: async () => {
            const shouldWarnAboutQualifiedSignature = this.item.envelope.signatureType === 'qualified' && !this.qualifiedDSWarningIsMuted
            if (shouldWarnAboutQualifiedSignature) {
              this.qualifiedDSWarningDialog = true
            } else {
              this.openSendSignDialog()
            }
          },
        })
      }
      if (canShowSendStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.sendToSign'),
          locatorValue: 'send-to-sign-menu-item',
          action: () => {
            const shouldWarnAboutQualifiedSignature = this.item.envelope.signatureType === 'qualified' && !this.qualifiedDSWarningIsMuted
            if (shouldWarnAboutQualifiedSignature) {
              this.qualifiedDSWarningDialog = true
            } else {
              this.openSendSignDialog()
            }
          },
        })
      }

      if (canShowCancelStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.cancel'),
          locatorValue: 'cancel-menu-item',
          action: () => {
            this.SET_CANCEL_STEP_MODAL({ ids: [this.item.id] })
          },
        })
      }

      if (canShowResetToDraftStep(this.item)) {
        menu.push({
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.menu.voidedToDraft'),
          locatorValue: 'reset-to-draft-menu-item',
          action: () => {
            this.SET_VOIDED_TO_DRAFT_STEP_MODAL({
              steps: [this.item],
              parent: this.parent,
              ids: [this.item.envelope.id],
            })
          },
        })
      }

      return menu
    },
    isMultiEnvelope () {
      return this.item.envelope.envelopeType === 'multiple'
    },
    isRefreshing () {
      return this.refreshStepPendingIds.includes(this.item.id)
    },
    isStepSelected () {
      return this.selectedSteps.some(item => item.id === this.item.id && item.type === this.item.type)
    },
    computedTitle () {
      return `${this.item.display} ${this.item.title || ''}`
    },
    floatingMenu () {
      return [
        {
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.addStep'),
          action: () => {
            this.$router.push({
              name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
              query: {
                prevId: this.item.id,
                prevType: this.item.type,
              },
            })
          },
        },
        {
          title: this.$t('project.signing-checklist.list.SigningChecklistTableItem.addTitle'),
          action: async () => {
            const payload = {
              prevId: this.item.id,
              prevType: 'step',
              depth: this.item.depth,
              title: '',
            }
            const newTitle = await this.CREATE_TITLE({
              mnemo: this.mnemo,
              data: payload,
              parent: this.parent,
            })
            this.SET_CURRENT_TITLE_EDIT(newTitle)
            this.REFRESH_SIGNING_CHECKLIST({
              mnemo: this.mnemo,
            })
          },
          loading: this.createTitlePending,
        },
      ]
    },
    isDisplayed () {
      return !this.isSigningChecklistFiltered || (this.isSigningChecklistFiltered && this.isMatchingFilters)
    },
    allWitnessFlattened () {
      return this.item.envelope.witnesses.flat()
    },
    isErrorStatusFaqLinkDisplayed() {
      return this.$i18n.locale === 'fr' && this.errorStatusFaqLink
    },
  },
  methods: {
    ...mapMutations([SET_CURRENT_TITLE_EDIT, SET_DELETE_STEP_MODAL, SET_SWAP_FILES, ADD_REFRESH_STEP_PENDING, REMOVE_REFRESH_STEP_PENDING, SET_CANCEL_STEP_MODAL, SET_SEND_SIGN_STEP_MODAL, SET_VOIDED_TO_DRAFT_STEP_MODAL]),
    ...mapActions([REFRESH_STEP, VALIDATE_THEN_SEND_NOW, CREATE_TITLE, REFRESH_SIGNING_CHECKLIST, OPEN_SHARE_ATTACHMENT_FILE_PANE]),
    onSelectedStepClick () {
      this.$store.commit(`signingChecklist/${TOGGLE_SELECTED_STEP}`, this.item)
    },
    getValidationDate (date) {
      return ISOToShortDate(date)
    },
    openShareAttachmentFilePane (attachmentFile) {
      this.OPEN_SHARE_ATTACHMENT_FILE_PANE(attachmentFile)
    },
    openSendSignDialog () {
      this.SET_SEND_SIGN_STEP_MODAL({
        steps: [this.item],
        parent: this.parent,
        ids: [this.item.id],
      })
    },
    confirmQualifiedSignatureWarning () {
      this.qualifiedDSWarningDialog = false
      this.openSendSignDialog()
    },
    getSignerWitness (signerId) {
      return this.allWitnessFlattened.filter(w => w.witnessOfSignerId === signerId)
    },
    hasSignerWitness (signerId) {
      return this.getSignerWitness(signerId).length > 0
    },
    handleSignAsWitness (envelopeId) {
      // TODO : This is a temporary workaround until we decide how to handle router links on the new design systems buttons
      this.$router.push({
        name: SIGNING_VIEW_ROUTE_NAME,
          params: {
            envelopeId: envelopeId,
          },
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.SigningChecklistTableItem {
  background-color: #f5f5f5;
  border: 1px solid #eee;
  border-radius: 4px;
  font-size: 14px;
  
  @media #{map-get($display-breakpoints, 'sm-and-down')}{
    flex-direction: column;

    .v-col {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

.SigningChecklistTableItem-status {
  border-left: 8px solid transparent;

  @media #{map-get($display-breakpoints, 'sm-and-down')}{
    border: none;
  }
  
  &--draft,
  &--temp {
    border-left-color: #e0e0e0;
  }

  &--sent {
    border-left-color: rgb(var(--v-theme-warning));
  }

  &--signed {
    border-left-color: rgb(var(--v-theme-success));
  }

  &--queued {
    border-left-color: rgb(var(--v-theme-warning));
  }

  &--error,
  &--voided {
    border-left-color: rgb(var(--v-theme-error));
  }

  &--unvalidated {
    border-left-color: rgb(var(--v-theme-warning));
  }
}

.SigningChecklistTableItem.sortable-ghost,
.SigningChecklistTableItem.sortable-chosen,
.SigningChecklistTableItem.sortable-drag {
  background-color: rgb(var(--v-theme-primary-lighten-4)) !important;
  border: 2px dashed rgb(var(--v-theme-primary)) !important;
}

.marker {
  position: absolute;
  bottom: -17px;
  left: -20px;
  cursor: pointer;
  opacity: 0.5;
  font-size: 26px;
  color: rgb(var(--v-theme-primary));
  z-index: 1;
}

.marker:hover {
  opacity: 1;
}

.SigningChecklistTableItem-mobileRow {
  padding-left: 15px;
  padding-right: 15px;
  width: 100%;
}

.SigningChecklistTableItem-mobileRowGrid {
  padding-left: 15px;
  padding-right: 15px;
  display: grid;
  grid-template-columns: 1fr 2fr;
  width: 100%;
}

.SigningChecklistTableItem-mobileRow-separator {
  width: 100%;
  height: 2px;
  margin-top: 12px;
  margin-bottom: 12px;
  background-color: #eee;
}

:deep(.SigningChecklistTableItem-select) {
  .v-input__slot {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  .v-input__append-inner {
    padding-left: 0;
  }

  .v-btn {
    width: 16px;
    height: 20px;
    padding-right: 5px !important;
    padding-left: 5px !important;
  }
}

.item-name {
  overflow-wrap: anywhere;
}

.attachment-file-actions {
  display: flex;
  position: relative;
  left: -7px;
}
.checkbox-drag-icon-wrapper {
  min-width: 60px; // To avoid layout shift when documents have long titles
}
</style>
